import type React from "react"
import { SkeletonAccordion } from "./SkeletonAccordion"
import { SkeletonButtonRound } from "./SkeletonButtonRound"
import { SkeletonCheckbox } from "./SkeletonCheckbox"
import { SkeletonCheckboxGroup } from "./SkeletonCheckboxGroup"
import { SkeletonColorTile } from "./SkeletonColorTile"
import type { SkeletonProps } from "./SkeletonComponent"
import { SkeletonComponent } from "./SkeletonComponent"
import { SkeletonHeading } from "./SkeletonHeading"
import { SkeletonInlineNotification } from "./SkeletonInlineNotification"
import { SkeletonInput } from "./SkeletonInput"
import { SkeletonList } from "./SkeletonList"
import { SkeletonPicture } from "./SkeletonPicture"
import { SkeletonRadioButton } from "./SkeletonRadioButton"
import { SkeletonRadioGroup } from "./SkeletonRadioGroup"
import { SkeletonSwitch } from "./SkeletonSwitch"
import { SkeletonTable } from "./SkeletonTable"
import { SkeletonTabsBar } from "./SkeletonTabsBar"
import { SkeletonTag } from "./SkeletonTag"
import { SkeletonTagDismissible } from "./SkeletonTagDismissible"
import { SkeletonText } from "./SkeletonText"
import { SkeletonTextarea } from "./SkeletonTextarea"

const SkeletonComp = SkeletonComponent as React.FC<SkeletonProps> & {
	Accordion: typeof SkeletonAccordion
	ButtonRound: typeof SkeletonButtonRound
	Checkbox: typeof SkeletonCheckbox
	CheckboxGroup: typeof SkeletonCheckboxGroup
	ColorTile: typeof SkeletonColorTile
	Heading: typeof SkeletonHeading
	InlineNotification: typeof SkeletonInlineNotification
	Input: typeof SkeletonInput
	List: typeof SkeletonList
	Picture: typeof SkeletonPicture
	RadioButton: typeof SkeletonRadioButton
	RadioGroup: typeof SkeletonRadioGroup
	Switch: typeof SkeletonSwitch
	Table: typeof SkeletonTable
	TabsBar: typeof SkeletonTabsBar
	Tag: typeof SkeletonTag
	TagDismissible: typeof SkeletonTagDismissible
	Text: typeof SkeletonText
	Textarea: typeof SkeletonTextarea
}

SkeletonComp.Accordion = SkeletonAccordion
SkeletonComp.ButtonRound = SkeletonButtonRound
SkeletonComp.Checkbox = SkeletonCheckbox
SkeletonComp.CheckboxGroup = SkeletonCheckboxGroup
SkeletonComp.ColorTile = SkeletonColorTile
SkeletonComp.Heading = SkeletonHeading
SkeletonComp.InlineNotification = SkeletonInlineNotification
SkeletonComp.Input = SkeletonInput
SkeletonComp.List = SkeletonList
SkeletonComp.Picture = SkeletonPicture
SkeletonComp.RadioButton = SkeletonRadioButton
SkeletonComp.RadioGroup = SkeletonRadioGroup
SkeletonComp.Switch = SkeletonSwitch
SkeletonComp.Table = SkeletonTable
SkeletonComp.TabsBar = SkeletonTabsBar
SkeletonComp.Tag = SkeletonTag
SkeletonComp.TagDismissible = SkeletonTagDismissible
SkeletonComp.Text = SkeletonText
SkeletonComp.Textarea = SkeletonTextarea

/**
 * The `Skeleton` component can be used to indicate when data is not available yet.
 * It shows the user that the website is not frozen and still working on loading the content.
 *
 * Should be used when the format of missing information is predictable, so that the Skeleton can cover the same amount of space.
 * Available in different __themes__ and __sizes__ to fit the style.
 *
 * The Skeleton consists of `SkeletonHeading`, `SkeletonList`, `SkeletonText` and `SkeletonPicture`.
 */
export const Skeleton = SkeletonComp
